/**
 * Redaction for anything AppSignal receives from the BROWSER.
 *
 * Why this exists as its own module: the server-side agent scrubs request
 * params via the `filterParameters` list in appsignal.cjs, but that list
 *   (a) matches exact top-level PARAM key names, so it does nothing for a
 *       secret embedded in the *value* of a tag like `browser_url`, and
 *   (b) does not exist at all on the @appsignal/javascript path, where the
 *       browser posts straight to AppSignal without passing through our agent.
 *
 * So anything sourced from the browser must be redacted before it leaves,
 * by us, here. This module is pure and dependency-free so it can be imported
 * from client and server code alike.
 *
 * The concrete risk this closes: several routes carry secrets in the query
 * string —
 *   /reset-password?token_hash=…       (live Supabase recovery token)
 *   /login/verify?email=…              (also signup/verify, login/setup)
 *   /events/[slug]?guest_email=…&booking_id=…
 * The App Router error boundaries (app/error.tsx, app/global-error.tsx) wrap
 * every route and report on ANY render error, so without redaction an
 * unrelated null-ref on the reset-password page would ship a working
 * password-reset token to a third party.
 */

export const REDACTED = '[FILTERED]';

/**
 * Param/tag keys whose values must never be transmitted. Mirrors the intent of
 * `filterParameters` in appsignal.cjs — keep the two in sync when either
 * changes. Compared case-insensitively, unlike the agent's exact matching,
 * because browser-supplied keys are not under our control.
 */
const SENSITIVE_KEYS: ReadonlySet<string> = new Set(
  [
    'password',
    'currentPassword',
    'newPassword',
    'token',
    'token_hash',
    'tokenHash',
    'access_token',
    'refresh_token',
    'code',
    'authorization',
    'cookie',
    'apiKey',
    'client_secret',
    'email',
    'guest_email',
    'phone',
    'phoneNumber',
    'card',
    'cardNumber',
    'cvc',
    'dateOfBirth',
    'dob',
    'idNumber',
    'ssn',
    'taxId',
    'streetAddress',
    'street',
    'building',
    'city',
    'postcode',
    'postalCode',
    'addressLine1',
    'addressLine2',
    'bankAccount',
    'bankAccountNumber',
    'routingNumber',
  ].map((k) => k.toLowerCase()),
);

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(key.toLowerCase());
}

/**
 * Param keys whose VALUES may be transmitted. Everything else is filtered.
 *
 * `SENSITIVE_KEYS` above is a denylist, which is the right shape for breadcrumb
 * metadata — the keys there come from the SDK's own plugins, so the vocabulary
 * is small and known. It is the wrong shape for `redactParams`, whose input is
 * whatever a caller passed. A denylist there fails open: `attendeeName`,
 * `notes`, `message`, `bio`, `dietaryRequirements` and every key nobody has
 * thought of yet sail through to a third party untouched, and the list only
 * grows after someone notices.
 *
 * So params fail closed. This list is deliberately boring: identifiers that are
 * already opaque, and operational values that describe the request rather than
 * the person making it. If a value would be uncomfortable in a third-party log,
 * it does not belong here.
 *
 * Filtered keys keep their NAME — knowing an `attendeeName` was present is
 * useful for reproducing a bug; knowing it was "Ada Lovelace" is not.
 */
const SAFE_PARAM_KEYS: ReadonlySet<string> = new Set(
  [
    // Opaque identifiers. Not secrets, and useless without our database.
    'id',
    'eventId',
    'event_id',
    'bookingId',
    'booking_id',
    'userId',
    'user_id',
    'facilitatorId',
    'facilitator_id',
    'ticketTypeId',
    'ticket_type_id',
    'seriesId',
    'series_id',
    'attemptId',
    'attempt_id',
    'ticketId',
    'ticket_id',
    'requestId',
    'request_id',
    'traceId',
    'trace_id',
    'sessionId',
    'session_id',
    // Request shape.
    'method',
    'route',
    'path',
    'pathname',
    'endpoint',
    'status',
    'statusCode',
    'status_code',
    'operation',
    'action',
    'step',
    'attempt',
    'retryCount',
    'retry_count',
    // Domain enums and flags — bounded vocabularies, not free text.
    'kind',
    'type',
    'role',
    'provider',
    'currency',
    'locale',
    'source',
    'reason',
    'severity',
    'environment',
    'feature',
    'flag',
    'enabled',
    // Counts and timings.
    'count',
    'quantity',
    'total',
    'page',
    'perPage',
    'per_page',
    'limit',
    'offset',
    'durationMs',
    'duration_ms',
    'elapsedMs',
    'elapsed_ms',
    // OAuth provider diagnostic text — an identity provider's own
    // account-of-what-went-wrong on a failed callback exchange (Supabase's
    // `error_description`), never something a user typed.
    //
    // Namespaced on purpose. This entry is the ONE exception to the rule
    // above that everything here is safe on its own merits: it is free text,
    // and it is only safe because its single call site truncates it and runs
    // it through `scrubText` first (see CallbackExchanger.tsx). A bare
    // `error_description` key would silently bless raw text from any future
    // caller that happened to pick the obvious name; `oauth_error_description`
    // unlocks only the one pre-scrubbed path. Do not reuse it for anything
    // that has not been through the same treatment.
    'oauth_error_description',
  ].map((k) => k.toLowerCase()),
);

function isSafeParamKey(key: string): boolean {
  return SAFE_PARAM_KEYS.has(key.toLowerCase());
}

/**
 * Rewrites a query string so the KEY names survive (they are genuinely useful
 * for triage — "was this the invite flow?") while every value is dropped.
 *
 * Values are redacted wholesale rather than only for known-sensitive keys: the
 * set of query params in the app changes faster than this list would, and a
 * missed key fails open. Key names alone are enough to reproduce a bug.
 */
export function redactSearch(search: string | undefined): string | undefined {
  if (!search) return undefined;
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return undefined;
  const parts = raw.split('&').filter(Boolean);
  if (!parts.length) return undefined;
  return `?${parts
    .map((part) => {
      const key = part.split('=')[0] ?? '';
      return key ? `${key}=${REDACTED}` : REDACTED;
    })
    .join('&')}`;
}

/**
 * Strips the query string and fragment from a URL, keeping origin + path.
 * Falls back to a plain truncation for anything unparseable rather than
 * risking passing the original through.
 */
export function redactUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    // Not absolute (or malformed) — cut at the first ? or # by hand.
    const cut = url.search(/[?#]/);
    return cut === -1 ? url : url.slice(0, cut);
  }
}

/**
 * Keeps values under known-safe keys and filters everything else, recursively.
 *
 * Recursion matters here specifically because the browser SDK path has no
 * agent-side scrubbing to fall back on, and AppSignal only documents
 * nested-key filtering for its Ruby integration.
 *
 * Nested objects are still walked rather than filtered whole: the SHAPE of a
 * param is what makes a report reproducible, and a wall of `[FILTERED]` under
 * the right key names is far more useful for triage than a single one at the
 * top. Recursion is bounded and non-plain objects are described rather than
 * traversed, both unchanged from before.
 */
/**
 * True for objects we can safely walk as key/value records.
 *
 * Recursing into a Date, Map, Set, Error or class instance would return `{}`
 * (their data isn't in own enumerable string keys), silently destroying the
 * value mid-triage. Those are stringified instead — lossy, but readable, and
 * honest about what it is.
 */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function describeNonPlain(value: object): string {
  try {
    if (value instanceof Date) return value.toISOString();
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    if (value instanceof Map) return `[Map size=${value.size}]`;
    if (value instanceof Set) return `[Set size=${value.size}]`;
    return `[${value.constructor?.name ?? 'Object'}]`;
  } catch {
    return '[unserialisable]';
  }
}

export function redactParams(
  params: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  // Bound the walk: params come from callers and could be cyclic or huge.
  if (depth > 6) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    // Containers are walked regardless of their key so nested safe values stay
    // visible; the leaves inside them are held to the same allowlist.
    if (Array.isArray(value)) {
      const safe = isSafeParamKey(key);
      // Typed as unknown[] explicitly: `Array.isArray` on an `unknown` narrows
      // to `any[]` under this tsconfig, which would make every element `any`
      // and trip no-unsafe-return / no-unsafe-argument downstream.
      out[key] = (value as unknown[]).map((v: unknown) =>
        isPlainRecord(v)
          ? redactParams(v, depth + 1)
          : v && typeof v === 'object'
            ? describeNonPlain(v)
            : // A scalar in an array inherits its parent key's verdict —
              // `attendeeNames: ['Ada', 'Grace']` must not leak because the
              // elements happen to be strings rather than an object.
              safe
              ? v
              : REDACTED,
      );
    } else if (isPlainRecord(value)) {
      out[key] = redactParams(value, depth + 1);
    } else if (value && typeof value === 'object') {
      out[key] = describeNonPlain(value);
    } else {
      out[key] = isSafeParamKey(key) ? value : REDACTED;
    }
  }
  return out;
}

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
/**
 * Query strings AND fragments on any URL-looking substring. The fragment half
 * matters specifically for Supabase auth, which returns tokens after a `#`
 * (`/auth/callback#access_token=…&refresh_token=…`) rather than a `?`, so a
 * query-only pattern would miss the most sensitive case entirely.
 */
// `)` is excluded from both halves because stack frames wrap the URL in
// parens — `at foo (https://…/x.js?v=1:10:20)` — and swallowing the `)` would
// stop the trailing `:line:col` from being detected and preserved below.
const URL_QUERY_RE =
  /(https?:\/\/[^\s"'<>)]+?|\/[^\s"'<>)?#]*)[?#][^\s"'<>)]*/g;
/**
 * Key names that must never appear next to their value in free text.
 *
 * Ordered longest-first: regex alternation is leftmost-first, so `token` ahead
 * of `token_hash` would match the prefix and leave `_hash=<secret>` behind.
 */
const SECRET_KEYS =
  'token_hash|access_token|refresh_token|client_secret|api_?key|authorization|password|secret|session|bearer|token|code|jwt';

/**
 * Bare `key=value` secrets with no surrounding URL — e.g. a token pasted into
 * a console.error argument. Backstop for the URL pattern above.
 */
const TOKEN_PARAM_RE = new RegExp(`\\b(${SECRET_KEYS})=[^\\s&"'<>]+`, 'gi');

/**
 * The same secrets in OBJECT notation rather than query notation.
 *
 * This is what makes `consoleBreadcrumbsPlugin` safe to ship. That plugin
 * serialises every console argument, so `console.error('auth failed', { … })`
 * arrives as `{"refresh_token":"…"}` — a COLON, not the `=` the pattern above
 * requires. Without this, an opaque (non-JWT) token logged alongside a message
 * reached AppSignal verbatim; only JWT-shaped values were caught, and then
 * only by accident of `JWT_RE`.
 *
 * Three value shapes are covered because serialisers differ: JSON.stringify
 * emits `"key":"value"`, util.inspect-style output emits `key: 'value'`, and
 * an unquoted scalar can appear either way.
 */
const TOKEN_JSON_DQ_RE = new RegExp(
  `(["']?(?:${SECRET_KEYS})["']?\\s*:\\s*)"[^"]*"`,
  'gi',
);
const TOKEN_JSON_SQ_RE = new RegExp(
  `(["']?(?:${SECRET_KEYS})["']?\\s*:\\s*)'[^']*'`,
  'gi',
);
const TOKEN_JSON_BARE_RE = new RegExp(
  `(["']?(?:${SECRET_KEYS})["']?\\s*:\\s*)(?!["'])[A-Za-z0-9._~+/=-]+`,
  'gi',
);
/**
 * Bare JWTs with no `key=` prefix, e.g. `console.error('auth failed', token)`
 * or an `Authorization: Bearer …` value. Three base64url segments.
 */
const JWT_RE = /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g;

/**
 * Scrubs free text that may embed a secret. Used for breadcrumb strings,
 * where the content is arbitrary (console arguments, request URLs) so
 * key-based redaction cannot apply.
 *
 * Deliberately conservative and lossy: it strips query strings wholesale
 * rather than trying to keep "safe" params, and masks anything email-shaped.
 */
export function scrubText(value: string): string {
  return value
    .replace(TOKEN_PARAM_RE, (m) => `${m.split('=')[0]}=${REDACTED}`)
    // Object notation before the URL pass: a serialised object can contain a
    // URL-looking substring, and cutting that first would leave the key/value
    // pair unrecognisable to these patterns.
    .replace(TOKEN_JSON_DQ_RE, (_m, prefix: string) => `${prefix}"${REDACTED}"`)
    .replace(TOKEN_JSON_SQ_RE, (_m, prefix: string) => `${prefix}'${REDACTED}'`)
    .replace(TOKEN_JSON_BARE_RE, (_m, prefix: string) => `${prefix}${REDACTED}`)
    .replace(URL_QUERY_RE, (m) => {
      const cut = m.search(/[?#]/);
      if (cut === -1) return m;
      // Preserve a trailing `:line:col` if V8 appended one. Stack frames look
      // like `at https://app.test/_next/static/chunks/x.js?v=163:1:234` — a
      // naive cut at the `?` would delete the line and column, which is the
      // most useful part of the frame. Cache-busted chunk URLs and RSC fetch
      // URLs (`?_rsc=`) both hit this.
      const lineCol = /:(\d+):(\d+)$/.exec(m);
      const suffix = lineCol ? lineCol[0] : '';
      return `${m.slice(0, cut)}${m[cut]}${REDACTED}${suffix}`;
    })
    .replace(JWT_RE, REDACTED)
    .replace(EMAIL_RE, REDACTED);
}

/**
 * Breadcrumb metadata as the SDK types it: `HashMap<HashMapValue>`, i.e.
 * scalars only. Declared structurally rather than imported so this module
 * stays dependency-free and usable from server code.
 */
type BreadcrumbMetadata = Record<string, string | number | boolean>;

/**
 * Scrubs breadcrumb metadata before a span is transmitted.
 *
 * Must be applied from an `addOverride` hook, not `addDecorator`: in
 * @appsignal/javascript's `send()`, breadcrumbs are attached to the span
 * (`span.setBreadcrumbs`) AFTER decorators run and BEFORE overrides run, so
 * decorators cannot see them.
 *
 * Neither breadcrumb plugin exposes a value filter of its own —
 * networkBreadcrumbsPlugin records the full URL in `metadata.url` and
 * `ignoreUrls` can only drop an entire breadcrumb, while
 * consoleBreadcrumbsPlugin serialises every console argument verbatim.
 */
export function scrubBreadcrumbs<T extends { metadata?: BreadcrumbMetadata }>(
  crumbs: readonly T[],
): T[] {
  return crumbs.map((crumb) => {
    if (!crumb.metadata) return crumb;
    const metadata: BreadcrumbMetadata = {};
    for (const [key, value] of Object.entries(crumb.metadata)) {
      if (isSensitiveKey(key)) {
        metadata[key] = REDACTED;
      } else if (typeof value === 'string') {
        metadata[key] = scrubText(value);
      } else {
        metadata[key] = value;
      }
    }
    return { ...crumb, metadata };
  });
}

/**
 * Tag names owned by the reporters. A caller — including an unauthenticated
 * POST to /api/exceptions — must never set them, because triage trusts them.
 */
export const RESERVED_TAGS: ReadonlySet<string> = new Set([
  'userId',
  'filename',
  'line',
  'handler',
  'environment',
  'source',
]);
